import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {entryNameProblem} from '../../utils/treeEntryNames';

/**
 * The name of a tree row, editable in place (Rename, F2 or Enter on the selected row): Enter or leaving the field
 * renames, Escape puts the name back. A name that cannot be used (empty, with "/", already in the folder) is said
 * under the field on Enter and nothing is sent; leaving the field then puts the old name back. The name is selected
 * without its extension, like in a file manager.
 */
const TreeRenameInput = ({name, siblings, onCommit, onCancel}) => {
  const [value, setValue] = useState(name);
  const [problem, setProblem] = useState(null);
  const inputRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) { return; }
    input.focus();
    const dot = name.lastIndexOf('.');
    input.setSelectionRange(0, dot > 0 ? dot : name.length);
  }, [name]);

  const finish = (commit) => {
    if (doneRef.current) { return; }
    const trimmed = value.trim();
    if (!commit || trimmed === name) {
      doneRef.current = true;
      onCancel();
      return;
    }
    const why = entryNameProblem(trimmed, {siblings, current: name});
    if (why) {
      setProblem(why);
      return;
    }
    doneRef.current = true;
    onCommit(trimmed);
  };

  const onKeyDown = (event) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      finish(true);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      finish(false);
    }
  };

  return (
    <span className="tree-rename">
      <input
        aria-describedby={problem ? 'tree-rename-problem' : undefined}
        aria-invalid={Boolean(problem)}
        aria-label={`New name for ${name}`}
        className={`tree-rename-input${problem ? ' is-invalid' : ''}`}
        data-testid="tree-rename-input"
        // Leaving the field keeps a name that can be used, and puts the old one back otherwise.
        onBlur={() => finish(!entryNameProblem(value, {siblings, current: name}))}
        onChange={(event) => { setValue(event.target.value); setProblem(null); }}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
        ref={inputRef}
        spellCheck={false}
        type="text"
        value={value}
      />
      {problem && <span className="tree-rename-problem" id="tree-rename-problem" role="alert">{problem}</span>}
    </span>
  );
};

TreeRenameInput.propTypes = {
  name: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  siblings: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default TreeRenameInput;
